import { useRef, useState, useEffect, useCallback } from 'react';
import { ReactSketchCanvas, ReactSketchCanvasRef } from 'react-sketch-canvas';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { 
  Pen, 
  Eraser, 
  Undo2, 
  Redo2, 
  Trash2, 
  Download,
  Check,
  X,
  GripHorizontal,
  Fingerprint,
  GitBranch,
  Network,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DiagramCanvas } from './diagram-canvas';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface DrawingCanvasProps {
  initialData?: string;
  onSave?: (data: string, height: number) => void;
  onCancel?: () => void;
  height?: number;
  onHeightChange?: (height: number) => void;
  readOnly?: boolean;
  defaultMode?: 'freehand' | 'diagram';
  isFullscreen?: boolean;
  onFullscreenChange?: (fullscreen: boolean) => void;
  onAutoSave?: (data: string) => void;
}

interface UnifiedCanvasData {
  mode: 'freehand' | 'diagram';
  freehandData?: string;
  diagramData?: string;
  height?: number;
}

const COLORS = [
  { name: 'Black', value: '#000000' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Green', value: '#10B981' },
  { name: 'Yellow', value: '#FACC15' },
  { name: 'Purple', value: '#A855F7' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Pink', value: '#EC4899' },
];

const STROKE_WIDTHS = [
  { name: 'Thin', value: 2 },
  { name: 'Medium', value: 4 },
  { name: 'Thick', value: 8 },
  { name: 'Extra Thick', value: 12 },
];

function FreehandCanvas({ 
  initialData, 
  onSave, 
  onCancel,
  height: initialHeight = 400,
  onHeightChange,
  readOnly = false
}: {
  initialData?: string;
  onSave?: (data: string, height: number) => void;
  onCancel?: () => void;
  height?: number;
  onHeightChange?: (height: number) => void;
  readOnly?: boolean;
}) {
  const canvasRef = useRef<ReactSketchCanvasRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [eraserMode, setEraserMode] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [canvasHeight, setCanvasHeight] = useState(initialHeight);
  const [isResizing, setIsResizing] = useState(false);
  const [stylusOnlyMode, setStylusOnlyMode] = useState(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (readOnly) return;
    e.preventDefault();
    setIsResizing(true);
    startYRef.current = e.clientY;
    startHeightRef.current = canvasHeight;
  }, [canvasHeight, readOnly]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    const deltaY = e.clientY - startYRef.current;
    const newHeight = Math.max(200, Math.min(1200, startHeightRef.current + deltaY));
    setCanvasHeight(newHeight);
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
      onHeightChange?.(canvasHeight);
    }
  }, [isResizing, canvasHeight, onHeightChange]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isResizing, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (initialData && canvasRef.current && !isLoaded) {
      try {
        const parsed = JSON.parse(initialData);
        canvasRef.current.loadPaths(parsed);
        setIsLoaded(true);
      } catch (error) {
        console.error('Failed to load drawing data:', error);
      }
    }
  }, [initialData, isLoaded]);


  const handleUndo = () => {
    canvasRef.current?.undo();
  };

  const handleRedo = () => {
    canvasRef.current?.redo();
  };

  const handleClear = () => {
    if (confirm('Clear all drawing? This cannot be undone.')) {
      canvasRef.current?.clearCanvas();
    }
  };

  const handleExport = async () => {
    try {
      const svg = await canvasRef.current?.exportSvg();
      if (svg) {
        const blob = new Blob([svg], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `drawing-${Date.now()}.svg`;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to export drawing:', error);
    }
  };

  const handleSave = async () => {
    try {
      const paths = await canvasRef.current?.exportPaths();
      if (paths && onSave) {
        onSave(JSON.stringify(paths), canvasHeight);
      }
    } catch (error) {
      console.error('Failed to save drawing:', error);
    }
  };

  const toggleEraser = () => {
    const newEraserMode = !eraserMode;
    setEraserMode(newEraserMode);
    
    if (canvasRef.current) {
      canvasRef.current.eraseMode(newEraserMode);
    }
    
    if (!newEraserMode) {
      setStrokeColor(strokeColor);
    }
  };

  return (
    <>
      {!readOnly && (
      <div className="drawing-toolbar flex flex-wrap items-center gap-2 p-2 border-b bg-gray-50 dark:bg-gray-800">
        <Button
          variant={eraserMode ? 'default' : 'ghost'}
          size="sm"
          onClick={toggleEraser}
          title="Eraser"
          data-testid="button-eraser"
        >
          <Eraser className="w-4 h-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        <Select value={strokeColor} onValueChange={setStrokeColor} disabled={eraserMode}>
          <SelectTrigger className="w-32 h-8" data-testid="select-color">
            <div className="flex items-center gap-2">
              <div 
                className="w-4 h-4 rounded border" 
                style={{ backgroundColor: strokeColor }}
              />
              <SelectValue />
            </div>
          </SelectTrigger>
          <SelectContent>
            {COLORS.map((color) => (
              <SelectItem key={color.value} value={color.value}>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded border" 
                    style={{ backgroundColor: color.value }}
                  />
                  <span>{color.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select 
          value={strokeWidth.toString()} 
          onValueChange={(v) => setStrokeWidth(Number(v))}
          disabled={eraserMode}
        >
          <SelectTrigger className="w-32 h-8" data-testid="select-stroke-width">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STROKE_WIDTHS.map((width) => (
              <SelectItem key={width.value} value={width.value.toString()}>
                {width.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Separator orientation="vertical" className="h-6" />

        <Button
          variant="ghost"
          size="sm"
          onClick={handleUndo}
          title="Undo"
          data-testid="button-undo"
        >
          <Undo2 className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRedo}
          title="Redo"
          data-testid="button-redo"
        >
          <Redo2 className="w-4 h-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          title="Clear All"
          data-testid="button-clear"
        >
          <Trash2 className="w-4 h-4" />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleExport}
          title="Export as SVG"
          data-testid="button-export"
        >
          <Download className="w-4 h-4" />
        </Button>

        <Separator orientation="vertical" className="h-6" />

        <Button
          variant={stylusOnlyMode ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setStylusOnlyMode(!stylusOnlyMode)}
          title={stylusOnlyMode ? "Stylus Only (finger input ignored)" : "All Input (finger + stylus)"}
          data-testid="button-stylus-only"
        >
          <Fingerprint className="w-4 h-4" />
          <span className="ml-1 text-xs hidden sm:inline">
            {stylusOnlyMode ? 'Stylus Only' : 'All Input'}
          </span>
        </Button>

        {onSave && onCancel && (
          <>
            <Separator orientation="vertical" className="h-6" />
            
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              title="Save Drawing"
              data-testid="button-save-drawing"
            >
              <Check className="w-4 h-4 mr-1" />
              Save
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              title="Cancel"
              data-testid="button-cancel-drawing"
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
          </>
        )}
      </div>
      )}

      <div 
        ref={containerRef}
        style={{ pointerEvents: readOnly ? 'none' : 'auto' }}
      >
        <ReactSketchCanvas
          ref={canvasRef}
          strokeWidth={strokeWidth}
          strokeColor={strokeColor}
          canvasColor="transparent"
          style={{
            border: 'none',
            width: '100%',
            height: `${canvasHeight}px`,
          }}
          eraserWidth={20}
          allowOnlyPointerType={stylusOnlyMode ? "pen" : "all"}
          withTimestamp={true}
        />
      </div>

      {!readOnly && (
        <div
          className={`flex items-center justify-center py-2 cursor-ns-resize border-t bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${isResizing ? 'bg-primary/20' : ''}`}
          onMouseDown={handleMouseDown}
          data-testid="canvas-resize-handle"
        >
          <GripHorizontal className="w-5 h-5 text-muted-foreground" />
          <span className="ml-2 text-xs text-muted-foreground select-none">
            Drag to resize ({canvasHeight}px)
          </span>
        </div>
      )}
    </>
  );
}

export function DrawingCanvas({ 
  initialData, 
  onSave, 
  onCancel,
  height: initialHeight = 400,
  onHeightChange,
  readOnly = false,
  defaultMode = 'freehand',
  isFullscreen,
  onFullscreenChange,
  onAutoSave,
}: DrawingCanvasProps) {
  const [diagramData, setDiagramData] = useState<string | undefined>();
  const [canvasHeight, setCanvasHeight] = useState(initialHeight);

  useEffect(() => {
    if (initialData) {
      try {
        const parsed = JSON.parse(initialData);
        if (parsed.mode === 'diagram' || parsed.nodes) {
          setDiagramData(initialData);
        } else if (parsed.freehandData) {
          setDiagramData(parsed.diagramData);
        } else {
          setDiagramData(undefined);
        }
        if (parsed.height) setCanvasHeight(parsed.height);
      } catch (error) {
        console.error('Failed to parse initial data:', error);
      }
    }
  }, [initialData]);

  const handleDiagramSave = (data: string) => {
    setDiagramData(data);
    const unified: UnifiedCanvasData = {
      mode: 'diagram',
      freehandData: undefined,
      diagramData: data,
      height: canvasHeight,
    };
    onSave?.(JSON.stringify(unified), canvasHeight);
  };

  const handleAutoSave = (data: string) => {
    setDiagramData(data);
    const unified: UnifiedCanvasData = {
      mode: 'diagram',
      freehandData: undefined,
      diagramData: data,
      height: canvasHeight,
    };
    onAutoSave?.(JSON.stringify(unified));
  };

  return (
    <DiagramCanvas
      initialData={diagramData}
      onSave={onSave ? handleDiagramSave : undefined}
      onCancel={onCancel}
      readOnly={readOnly}
      isFullscreen={isFullscreen}
      onFullscreenChange={onFullscreenChange}
      onAutoSave={onAutoSave ? handleAutoSave : undefined}
    />
  );
}
